import { apiAddCreditAutoTopUpConfig, apiDeleteCreditAutoTopUpConfig, apiGetCreditAutoTopUpConfig } from 'api/users/userCredits';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { queryKeys } from 'utils/react-query';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { useMemo } from 'react';

interface AutoTopUpConfig {
  topAmount: number;
  thresholdAmount: number;
}

interface MutationOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export const useCreditAutoTopUp = () => {
  const userId = useCurrentUserId();
  const queryClient = useQueryClient();

  const {
    data: autoTopUpData,
    isLoading: isLoadingAutoTopUp,
    refetch: refetchAutoTopUpConfig,
    error: autoTopUpError,
  } = useQuery([queryKeys.UserCreditAutoTopUpConfig], () => apiGetCreditAutoTopUpConfig({ userId }), {
    enabled: !!userId,
    retry: 2,
  });

  const {
    mutate: createMutate,
    isLoading: isCreatingAutoTopUp,
    error: createError,
  } = useMutation({
    mutationFn: (config: AutoTopUpConfig) =>
      apiAddCreditAutoTopUpConfig({
        userId,
        ...config,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.UserCreditAutoTopUpConfig]);
    },
  });

  const {
    mutate: deleteMutate,
    isLoading: isDeletingAutoTopUp,
    error: deleteError,
  } = useMutation({
    mutationFn: () => apiDeleteCreditAutoTopUpConfig({ userId }),
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.UserCreditAutoTopUpConfig]);
    },
  });

  const createOrUpdateAutoTopUp = (config: AutoTopUpConfig, options?: MutationOptions) => {
    createMutate(config, {
      onSuccess: () => {
        options?.onSuccess?.();
        refetchAutoTopUpConfig();
      },
      onError: (error) => {
        options?.onError?.(error);
      },
    });
  };

  // Delete auto top-up configuration
  const deleteAutoTopUp = (options?: MutationOptions) => {
    deleteMutate(undefined, {
      onSuccess: () => {
        options?.onSuccess?.();
        refetchAutoTopUpConfig();
      },
      onError: (error) => {
        options?.onError?.(error);
      },
    });
  };

  const isAutoTopUpEnabled = !!autoTopUpData?.credit_auto_top_up_config;

  const autoTopUpConfig = useMemo(() => {
    const config = autoTopUpData?.credit_auto_top_up_config;

    if (!config) {
      return {
        top_up_amount_cents: 0,
        threshold_amount_cents: 0,
      };
    }

    return {
      ...config,
      top_up_amount_cents: Number(config.top_up_amount_cents) + Number(config.threshold_amount_cents) || 0,
      threshold_amount_cents: Number(config.threshold_amount_cents) || 0,
    };
  }, [autoTopUpData?.credit_auto_top_up_config]);

  return {
    autoTopUpConfig,
    isAutoTopUpEnabled,

    isLoadingAutoTopUp,
    isCreatingAutoTopUp,
    isDeletingAutoTopUp,

    autoTopUpError,
    createError,
    deleteError,

    createOrUpdateAutoTopUp,
    deleteAutoTopUp,
    refetchAutoTopUpConfig,
  };
};
